import { gql, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getLocalCartWithProducts, getTotal } from '../common/helper';
import useValidateCart from './useValidateCart';
import useValidateCartGuest from './useValidateCartGuest';
import {formatLocalCart} from '../../../../hooks/useAuth';
import { formatNumberAsCurrency } from '../../../../utils/helper';
import useQueryParams from '../../../../hooks/useQueryParams';

function useCart() {
  const isAuthenticated = useSelector(state => state.Auth.isAuthenticated);
  const [cartData, setCartData] = useState(null);
  const localCart = useSelector(state => state.LocalCart.localCart);
  const [removedProductErrors, validateData] = useValidateCart({ cartData });
  const [guestValidateCartErrors] = useValidateCartGuest();

  const { loading, error, data } = useQuery(GET_ALL_CART_PRODUCTS, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network'
  });

  const {
    loading: loadingProducts,
    error: errorProducts,
    data: dataProducts
  } = useQuery(PRODUCTS_BY_IDS, {
    skip: isAuthenticated,
    variables: {
      ids: localCart.filter(x => !x.giftCard).map(x => x.product_id)
    }
  });

  const queryParams = useQueryParams();


  const {
    loading: loadingNextTier,
    error: errorNextTier,
    data: dataNextTier
  } = useQuery(NEXT_DISCOUNT_TIER, {
    variables: {
      cartProducts: formatLocalCart(localCart),
      forDate: queryParams.get('__testSale__') || null
    }
  });


  useEffect(() => {
    if (!loading && !loadingProducts) {
      const userCart = data && isAuthenticated ? data.getAllCartProducts : null;
      const localCartWithProducts = dataProducts
        ? getLocalCartWithProducts(dataProducts.productsByIds, localCart)
        : null;

      const updatedCartData = isAuthenticated
        ? userCart
        : localCartWithProducts;

      setCartData(updatedCartData);
    }
  }, [
    data,
    dataProducts,
    loadingProducts,
    loading,
    localCart,
    validateData?.validateCart?.data
  ]);

  const cartError = isAuthenticated ? error : errorProducts;
  const removedProductsErrors = isAuthenticated
    ? removedProductErrors
    : guestValidateCartErrors;

  const nextDiscountDisplay = (discountTier) => {
    if (discountTier.discount.type === 'percentage') {
      return `${discountTier.discount_value}%`;
    } else {
      return formatNumberAsCurrency(discountTier.discount_value);
    }
  }

  const { nextDiscountTier } = dataNextTier || {};
  const { discountTier } = nextDiscountTier || {};

  return {
    loading: !cartData,
    error: cartError,
    data: cartData,
    removedProductErrors: removedProductsErrors,
    nextDiscountTier: (!loadingNextTier && nextDiscountTier && cartData) ? {
      ...nextDiscountTier,
      discountDisplay : nextDiscountDisplay(discountTier),
      discountDelta   : discountTier.minimum_cart_total - getTotal(cartData)
    } : null
  };
}

export default useCart;

export const GET_ALL_CART_PRODUCTS = gql`
  query getAllCartProducts {
    getAllCartProducts {
      id
      quantity
      shippingService {
        id
        name
        price
      }
      giftCard {
        id
        user_id
        gift_card_design_id
        original_balance
        recipient_email
        personalized_message
        delivery_time
        giftCardDesign {
          id
          name
          image
          thumbnail
        }
      }
      extend_plan {
        id
        code
        title
        price
        term
      }
      product {
        id
        sku
        slug
        name
        full_name
        price
        lead_time
        shipping_window
        full_price
        is_swatch
        is_purchasable
        uses_inventory
        current_inventory
        inventory_threshold
        fabric {
          id
          is_available_on_pdp
          name
          treatment {
            id
            name
          }
          image
          collaborator {
            id
            name
          }
          material {
            id
            name
          }
        }
        leg {
          id
          name
        }
        casterLeg {
          id
          name
        }
        badge_treatment {
          id
          name
        }
        frame {
          id
          name
          orientation
          is_final_sale
          shippingServices {
            id
            name
            price
          }
          size {
            id
            is_available_on_pdp
            name
          }
          subcategories {
            id
            category {
              id
              name
            }
          }
        }
        images {
          id
          path
        }
      }
    }
  }
`;

const PRODUCTS_BY_IDS = gql`
  query productsByIds($ids: [Int!]!) {
    productsByIds(ids: $ids) {
      id
      sku
      slug
      name
      full_name
      price
      lead_time
      shipping_window
      full_price
      is_swatch
      is_purchasable
      uses_inventory
      current_inventory
      inventory_threshold
      supplier {
        id
        name
      }
      fabric {
        id
        is_available_on_pdp
        name
        treatment {
          id
          name
        }
        image
        collaborator {
          id
          name
        }
        material {
          id
          name
        }
      }
      leg {
        id
        name
      }
      casterLeg {
        id
        name
      }
      frame {
        id
        name
        orientation
        shippingServices {
          id
          name
          price
        }
        size {
          id
          is_available_on_pdp
          name
        }
        subcategories {
          id
          category {
            id
            name
          }
        }
      }
      pillowInsert {
        id
        name
      }
      images {
        id
        path
      }
    }
  }
`;

const NEXT_DISCOUNT_TIER = gql`
  query nextDiscountTier($cartProducts: String, $forDate: String) {
    nextDiscountTier(cartProducts: $cartProducts, forDate: $forDate) {
      discountTier {
        minimum_cart_total
        discount_value
        discount {
          code
          type
        }
      }
    }
  }
`
